export const isBuyApp = () => {
  return isAppType('buy');
};

export const isDeliverApp = () => {
  return isAppType('deliver');
};

export const isSellApp = () => {
  return isAppType('sell');
};

export const isToolsApp = () => {
  return isAppType('tools');
};

const isAppType = urlPrefix => {
  return new RegExp(`^/${urlPrefix}`).test(window.location.pathname);
};

const NEW_APP_BASE_URL_STORAGE_KEY = 'newAppBaseUrl';

// Each ui-application git worktree serves on its own port, so the target changes as you switch
// which branch you are verifying. This Vite server runs inside the web container and only reads
// import.meta.env at startup, so an env var would mean restarting it on every switch; localStorage
// is read per navigation instead. Set it from the browser console:
//   localStorage.setItem('newAppBaseUrl', 'http://localhost:8092')
const readStoredNewAppBaseUrl = () => {
  // Accessing localStorage throws under some privacy settings, and a throw here would break
  // navigation entirely — falling back to the env var or the default is always preferable.
  try {
    return window.localStorage.getItem(NEW_APP_BASE_URL_STORAGE_KEY);
  } catch {
    return null;
  }
};

const NEW_APP_PORT_PARAM = 'newAppPort';
const FIRST_LOCAL_UI_APP_PORT = 8080;
const LAST_LOCAL_UI_APP_PORT = 8099;

// A parallel agent cannot set localStorage in your browser, so ?newAppPort=8092 lets it hand over a
// single link that retargets one redirect at its own dev server.
//
// Only a port number is accepted, never a URL: this value reaches location.replace from an
// authenticated origin, so taking a caller-supplied URL would make the Classic UI an open redirect.
// The range check keeps it to ports a local dev server could plausibly own.
const requestedNewAppPortOrigin = () => {
  const requested = new URLSearchParams(window.location.search).get(
    NEW_APP_PORT_PARAM,
  );
  if (!/^\d+$/.test(requested ?? '')) {
    return null;
  }
  const port = Number(requested);
  if (port < FIRST_LOCAL_UI_APP_PORT || port > LAST_LOCAL_UI_APP_PORT) {
    return null;
  }
  return `http://localhost:${port}`;
};

// The NextGen UI is served under /app in production. In local dev it runs as a separate server,
// addressed by a newAppPort query param, else a localStorage override, else VITE_NGROK_UI_APP_URL
// (e.g. an ngrok tunnel), else localhost:8080.
export const newAppBaseUrl = () => {
  if (!import.meta.env.DEV) {
    return '/app';
  }
  const base =
    requestedNewAppPortOrigin() ||
    readStoredNewAppBaseUrl() ||
    import.meta.env.VITE_NGROK_UI_APP_URL ||
    'http://localhost:8080';
  return base.replace(/\/+$/, '');
};

/**
 * The given query string with the newAppPort parameter removed.
 *
 * The parameter addresses the Classic UI's redirect and is meaningless to the NextGen UI, so it is
 * dropped rather than forwarded.
 */
export const searchWithoutNewAppPort = search => {
  const params = new URLSearchParams(search);
  params.delete(NEW_APP_PORT_PARAM);
  const remaining = params.toString();
  return remaining ? `?${remaining}` : '';
};

export const resolveNewAppUrl = path => `${newAppBaseUrl()}${path}`;

/**
 * The given path with each `:name` placeholder replaced by the matching route parameter.
 *
 * A placeholder with no matching parameter is left as-is rather than emptied, so a bad target is
 * visible in the URL instead of silently resolving to a different resource.
 */
export const replaceUrlParameter = (url, params) =>
  url.replace(/:(\w+)/g, (match, key) =>
    params[key] !== undefined ? params[key] : match,
  );
